import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '@/services/api';

interface Props {
  assetId: string;
  defaultDateFrom: string;
  defaultDateTo: string;
  onClose: () => void;
}

export default function ForecastExportModal({ assetId, defaultDateFrom, defaultDateTo, onClose }: Props) {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [interval, setInterval] = useState<'hour' | '30min'>('hour');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    if (dateFrom > dateTo) {
      setError(t('fc_err_date_order'));
      return;
    }
    setError(null);
    setExporting(true);

    const url = `${API_BASE_URL}/forecasting/assets/${assetId}/forecast-comparison/export.xlsx?dateFrom=${dateFrom}&dateTo=${dateTo}&interval=${interval}`;

    try {
      const res = await fetch(url, {
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });

      if (!res.ok) {
        let errMsg = t('fc_err_export_failed');
        const ct = res.headers.get('content-type') ?? '';
        if (ct.includes('application/json')) {
          try {
            const body = await res.json();
            errMsg = body.message ?? body.error ?? errMsg;
          } catch {
            // ignore parse error
          }
        }
        throw new Error(errMsg);
      }

      const blob = await res.blob();
      const cd = res.headers.get('content-disposition');
      let filename: string;
      const match = cd?.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']+)['"]?/);
      if (match?.[1]) {
        filename = decodeURIComponent(match[1]);
      } else {
        filename = `helios-forecast-export-${dateFrom}-${dateTo}.xlsx`;
      }

      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('fc_err_export_failed'));
    } finally {
      setExporting(false);
    }
  }, [assetId, dateFrom, dateTo, interval, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      {/* Modal */}
      <div
        className="relative rounded-xl p-6 w-full max-w-md mx-4"
        style={{ background: '#1E1E22', border: '1px solid rgba(255,255,255,0.12)' }}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{t('fc_export_title')}</h2>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center cursor-pointer"
            style={{ color: '#6B6B70' }}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-close-line text-lg"></i>
            </div>
          </button>
        </div>

        <div className="space-y-4">
          {/* Date From */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#A0A0A8' }}>{t('fc_date_from')}</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setError(null); }}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{
                background: '#111113',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'var(--text)',
                fontFamily: 'Inter, sans-serif',
                colorScheme: 'dark',
              }}
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#A0A0A8' }}>{t('fc_date_to')}</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setError(null); }}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{
                background: '#111113',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'var(--text)',
                fontFamily: 'Inter, sans-serif',
                colorScheme: 'dark',
              }}
            />
          </div>

          {/* Interval selector */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#A0A0A8' }}>{t('fc_interval')}</label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="interval"
                  value="hour"
                  checked={interval === 'hour'}
                  onChange={() => setInterval('hour')}
                  className="accent-red-500"
                />
                <span className="text-sm text-white">{t('fc_mode_hourly')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" style={{ opacity: 0.4 }}>
                <input type="radio" name="interval" value="30min" disabled className="accent-red-500" />
                <span className="text-sm text-white">{t('fc_mode_30min')}</span>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}
                >{t('fc_coming_soon')}</span>
              </label>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--accent)' }}>
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line"></i>
              </div>
              {error}
            </div>
          )}

          {/* Loading */}
          {exporting && (
            <div className="flex items-center gap-2 text-xs" style={{ color: '#6B6B70' }}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin"></i>
              </div>{t('fc_preparing_export')}</div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: '#A0A0A8',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >{t('fc_cancel')}</button>
            <button
              onClick={handleDownload}
              disabled={exporting}
              className="px-4 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
              style={{
                background: 'color-mix(in srgb, var(--text) 10%, transparent)',
                color: 'var(--text)',
                border: '1px solid var(--line-strong)',
                opacity: exporting ? 0.6 : 1,
              }}
            >
              {exporting ? t('fc_preparing') : t('fc_download_xlsx')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}